import { createCachedSelector } from 're-reselect';
import moment from 'moment';

import { getWeek, getDay } from '@/utils/date';

import type { NotesItem } from '@/types/global';
import type { RootState } from '@/types/store/common';
import type { DateState } from '@/types/store/date';

export const getSortedNotes = createCachedSelector(
  (state: RootState) => state.notes,
  (state: RootState) => state.dateData.currentPage,
  (_state: RootState, dayInMs: number) => dayInMs,
  (notes, currentPage, dayInMs) => {
    if (Array.isArray(notes) && notes.length === 0) return [];

    const filteredNotes = filterNotes(notes, dayInMs, currentPage);

    const { reminded, notReminded } = filteredNotes.reduce(
      (obj, item) => {
        if (item.isReminded) {
          obj.reminded.push(item);
        } else {
          obj.notReminded.push(item);
        }
        return obj;
      },
      { reminded: [] as NotesItem[], notReminded: [] as NotesItem[] }
    );

    return reminded.concat(notReminded);
  }
)(
  (_state, dayInMs) => dayInMs // Cache selectors by state name
);

const filterNotes = (notes: NotesItem[], dayInMs: number, currentPage: DateState['currentPage']) => {
  switch (currentPage) {
    case 'week': {
      const [startOfWeek, , , , , , endOfWeek] = getWeek('current', dayInMs);
      return notes.filter(
        note =>
          note.status === 'active' ||
          (note.isReminded &&
            moment(note.dueDate, 'DD.MM.YYYY') >= startOfWeek &&
            moment(note.dueDate, 'DD.MM.YYYY') <= endOfWeek) ||
          (note.status === 'done' &&
            moment(note.doneAt) >= startOfWeek &&
            moment(note.doneAt) <= endOfWeek)
      );
    }
    case 'day': {
      const day = getDay('current', dayInMs);
      return notes.filter(
        note =>
          note.status === 'active' ||
          (note.isReminded &&
            moment(note.dueDate, 'DD.MM.YYYY') >= day &&
            moment(note.dueDate, 'DD.MM.YYYY') <= day) ||
          (note.status === 'done' && moment(note.doneAt) >= day && moment(note.doneAt) <= day)
      );
    }
    default: {
      return notes;
    }
  }
};
